import { QueryClient } from '@tanstack/react-query';
import { type Language } from '@models/i18n/language.model';
import { ErrorCode, type AppError } from '@models/errors/app-error.model';
import { API_CONFIG } from '@api/api.config';
import { initI18n } from '@i18n/i18n.service';
import { loadMasterData } from '@master-data/master-data.service';
import { retrievePnr, getPreferences } from '@api/landing/api/retrieve/retrieve.api';
import { useAppStore } from '@store/app.store';
import { useMasterDataStore } from '@store/master-data.store';
import { sessionStorageUtil } from '@utils/session-storage.util';
import { SESSION_KEYS } from '@utils/session-keys.constants';
import { logger } from '@utils/logger.util';
import {
  LEGACY_WEB_SESSION_QUERY_PARAM,
  WEB_SESSION_QUERY_PARAM,
} from '@utils/web-session-query.constants';
import { t } from 'i18next';

const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1_000;
const MINUTES_TO_MS = SECONDS_PER_MINUTE * MS_PER_SECOND;
const QUERY_STALE_TIME_MINUTES = 5;
const QUERY_GC_TIME_MINUTES = 10;
const QUERY_STALE_TIME_MS = QUERY_STALE_TIME_MINUTES * MINUTES_TO_MS;
const QUERY_GC_TIME_MS = QUERY_GC_TIME_MINUTES * MINUTES_TO_MS;
const RETRY_EXPONENT_BASE = 2;
const MAX_RETRY_DELAY_MS = 30_000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_STALE_TIME_MS,
      gcTime: QUERY_GC_TIME_MS,
      retry: 0,
      retryDelay: (attempt): number =>
        Math.min(API_CONFIG.RETRY_DELAY_MS * RETRY_EXPONENT_BASE ** attempt, MAX_RETRY_DELAY_MS),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

const buildFatalError = (message: string, code: ErrorCode): AppError => ({
  code,
  message,
  isFatal: true,
  timestamp: new Date().toISOString(),
});

const getBootstrapWebSessionId = (): string | null => {
  const storedWebSessionId = sessionStorageUtil.get<string>(SESSION_KEYS.WEB_SESSION_ID);
  if (typeof window === 'undefined') {
    return storedWebSessionId;
  }

  const params = new URLSearchParams(window.location.search);
  return (
    params.get(WEB_SESSION_QUERY_PARAM) ??
    params.get(LEGACY_WEB_SESSION_QUERY_PARAM) ??
    storedWebSessionId
  );
};

const isGroupBookingRoute = (): boolean =>
  typeof window !== 'undefined' && window.location.pathname.includes('/group-booking');

const preFetchPnrData = async (): Promise<void> => {
  if (isGroupBookingRoute()) {
    return;
  }

  const webSessionId = getBootstrapWebSessionId();
  if (!webSessionId) {
    return;
  }
  try {
    await queryClient.prefetchQuery({
      queryKey: ['retrieve', 'pnrMetaData'] as const,
      queryFn: retrievePnr,
    });
    logger.info('PNR data pre-fetched during bootstrap');
  } catch (error) {
    logger.warn('PNR pre-fetch failed during bootstrap, will retry on component mount', error);
  }
};

export const bootstrapApplication = async (urlLanguage: Language): Promise<void> => {
  const appStore = useAppStore.getState();
  const masterDataStore = useMasterDataStore.getState();

  // URL wins over session storage so a manually edited or shared language segment is respected.
  const storedLanguage = sessionStorageUtil.get<Language>(SESSION_KEYS.LANGUAGE);
  const language: Language = urlLanguage ?? storedLanguage;

  appStore.setLoading(true);
  appStore.setFatalError(null);

  // Persist resolved language to store + session storage
  appStore.setLanguage(language);

  try {
    logger.info(`Bootstrapping application with language: ${language}`);

    masterDataStore.hydrate();

    // Initialize i18n — non-fatal failure (single attempt, no retries)
    try {
      await initI18n(language);
      logger.info('i18n initialized');
    } catch (error) {
      logger.warn(
        'i18n initialization failed, app will continue with degraded translations',
        error,
      );
    }

    // Load master data — non-fatal failure (single attempt, no retries)
    try {
      await loadMasterData(language);
      logger.info('Master data loaded');
    } catch (error) {
      logger.warn('Master data loading failed, app will continue with cached or empty data', error);
      // Update store to indicate that master data is not fully loaded
      masterDataStore.setError('Failed to load master data. Some features may be unavailable.');
    }

    // Update document title with the new language's translation
    document.title = t('OLCI.HeaderPrefix.Text');

    if (!isGroupBookingRoute()) {
      try {
        const preferences = await getPreferences();
        useMasterDataStore.setState((state) => ({
          masterData: state.masterData ? { ...state.masterData, preferences } : null,
        }));
        logger.info('Preferences loaded');
      } catch (preferencesError) {
        logger.warn('Failed to fetch preferences', preferencesError);
      }
    }

    // Pre-fetch PNR data if web session id exists.
    // This populates React Query cache before passenger-details page mounts
    await preFetchPnrData();

    appStore.setBootstrapped(true);
    logger.info('Application bootstrap complete');
  } catch (error) {
    const fatalError = buildFatalError(
      'Application failed to initialize. Please refresh the page.',
      ErrorCode.BOOTSTRAP_FAILURE,
    );
    logger.error('Bootstrap failed', error);
    appStore.setFatalError(fatalError.message);
    throw error;
  } finally {
    appStore.setLoading(false);
  }
};
